import moment from "moment"
import "moment/locale/fr"

// Dérivations d'affichage de l'activation self-service de la suite (produit
// d'appel → offre régulière), sorties de l'écran pour être testables : ce sont
// des règles métier, pas de la mise en page.
//
// Règle centrale de l'urgence : la remise fidélité expire, PAS l'éligibilité.
// Passée la date limite, l'activation reste possible — seuls le compte à rebours
// et la date limite disparaissent. Aucune de ces fonctions ne doit donc jamais
// pouvoir bloquer le parcours ; au pire elles rendent `null`.

/**
 * Format long français d'une date, millésime inclus.
 *
 * Point unique de formatage des dates de l'activation : la locale est posée sur
 * l'INSTANCE et non globalement, parce que la locale globale de moment dépend de
 * l'ordre des imports d'écrans — un même libellé sortait en « 12 August 2026 »
 * ou « 12 août 2026 » selon le chemin de navigation.
 *
 * L'année est toujours affichée : le parcours chevauche la rentrée, et « du
 * 1ᵉʳ août au 31 août » sans millésime laisse le parent deviner de quelle
 * rentrée on parle.
 *
 * ORDINAL — en français, SEUL le premier du mois le prend : « 1ᵉʳ septembre »,
 * mais « 7 septembre » et jamais « 7ᵉ ». Le 1ᵉʳ n'est pas un cas limite sur
 * cette surface, c'est le cas nominal : la reprise par défaut de la rentrée
 * tombe au 1ᵉʳ septembre, et le format cardinal de moment écrivait « Reprise le
 * 1 septembre 2026 » à la quasi-totalité des familles.
 *
 * Une date invalide rend `null` comme une date absente, là où moment rendait
 * « Invalid date ». Les appelants qui gardent le `null` font disparaître leur
 * ligne ; ceux qui interpolent sans garde afficheront « null » — aussi cassé,
 * mais aucun des deux n'est un état atteignable avec une donnée saine, et un
 * `null` se repère en revue là où « Invalid date » se lit comme du contenu.
 */
export const formatActivationDate = (date) => {
    const parsed = date ? moment(date).locale("fr") : null
    if (!parsed?.isValid()) return null

    const day = parsed.date() === 1 ? "1ᵉʳ" : `${parsed.date()}`

    return `${day} ${parsed.format("MMMM YYYY")}`
}

/**
 * Montant en euros, sans centimes — « 87 € ».
 *
 * Les prix de l'activation sont des montants mensuels ronds côté back (grille
 * pricing v2 + remise fidélité en euros entiers) : afficher « 87,00 € » sur une
 * page d'abonnement ajoute du bruit sans information. Un montant absent rend
 * « — » plutôt que « 0 € », qui se lirait comme la gratuité.
 *
 * Ne pas confondre avec les `formatEuros` du parcours de résiliation et du
 * funnel brevet : ceux-là partent de CENTIMES et affichent les décimales.
 */
export const formatActivationPrice = (amount) =>
    amount == null
        ? "—"
        : new Intl.NumberFormat("fr-FR", {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
          }).format(amount)

/**
 * « de André » → « d'André ». Élision de la préposition devant un mot à
 * initiale vocalique.
 *
 * Une phrase adressée à un parent porte le prénom de son enfant : sans élision,
 * la page d'abonnement écrivait « la rentrée de André », une faute que
 * tout parent francophone lit immédiatement. Point unique de la règle pour
 * toutes les surfaces de l'app (carte du portail, bannière) ; son jumeau
 * back-end vit dans `TextHelper::elide()` pour le mail — même liste de
 * voyelles, mêmes arbitrages.
 *
 * Le H INITIAL est traité comme une voyelle (« d'Hugo », « d'Hélène »). Les
 * prénoms français à h aspiré sont rares au point d'être négligeables face aux
 * h muets courants, et le compromis inverse (« de Hugo ») serait faux beaucoup
 * plus souvent. Y suit la même règle (« d'Yasmine ») : en attaque de prénom il
 * est vocalique dans l'usage courant.
 */
export const elide = (name, preposition = "de") => {
    const trimmed = (name ?? "").trim()
    if (!trimmed) return preposition

    // Les diacritiques comptent : « Émile » commence bien par une voyelle.
    return /^[aeiouyhàâäéèêëîïôöùûü]/i.test(trimmed)
        ? `${preposition.replace(/e$/, "")}'${trimmed}`
        : `${preposition} ${trimmed}`
}

/**
 * Le mot de l'ÉCHÉANCE — « rentrée » ou « reprise ».
 *
 * « rentrée » n'est juste que pour les produits d'appel saisonniers (cours
 * d'été / stage), dont la suite tombe au 1ᵉʳ septembre. « reprise » reste donc
 * le défaut pour un produit borné dont la suite tomberait à une autre date.
 * Aucun type ne l'emprunte aujourd'hui (la fin d'engagement, seule autre
 * échéance lue, n'expose pas de parcours d'activation : l'abonnement s'y
 * poursuit sans rupture) ; la distinction est conservée par symétrie avec
 * l'évaluateur, qui garde lui aussi sa résolution multi-types pour n'avoir à
 * brancher qu'un type le jour où un nouveau produit borné arrive.
 *
 * Cette distinction ne vaut QUE pour le hero, qui parle de l'échéance. Tout ce
 * qui parle des CRÉNEAUX dit « reprise » quel que soit le produit : le parent y
 * choisit des horaires pour le jour où les lives repartent, pas pour un mois de
 * calendrier scolaire.
 */
const getActivationSeasonWord = (type) =>
    type === "cours_ete" || type === "stage" ? "rentrée" : "reprise"

/**
 * Accroche de l'activation — « On prépare la rentrée de Léa », « … d'André ».
 *
 * Même formule sur la bannière home et sur la carte du portail : le parent doit
 * reconnaître où il atterrit en cliquant.
 *
 * La formule dit ce que NOUS faisons déjà, là où « Réservez la rentrée »
 * demandait au parent d'agir avant de lui dire pourquoi : sur une famille qui
 * vient de terminer un produit d'appel, l'impératif se lisait comme une relance
 * commerciale de plus. L'effort, lui, est annoncé par la pastille du hero
 * (« 2 minutes suffisent ») — cf. getActivationHero.
 *
 * @param type      `upcoming_activation.type` (cours_ete | stage aujourd'hui).
 * @param firstName Prénom de l'élève ; absent → « votre enfant » (vouvoiement).
 */
export const getActivationHeadline = (type, firstName) =>
    `On prépare la ${getActivationSeasonWord(type)} ${elide(firstName || "votre enfant")}`

/**
 * Les trois lignes du HERO de la bannière home, dans l'ordre de lecture.
 *
 * Sorties ici plutôt qu'inlinées dans la bannière parce que la carte du portail
 * partage l'accroche : deux formulations à deux endroits, c'est la dérive que
 * cette page a déjà connue. La bannière ne fait que rendre ce descripteur.
 *
 * `subline` retombe sur une accroche NEUTRE quand la date limite est absente
 * (remise expirée ou non datée) : la bannière promettait « tarif fidélité
 * inclus » alors que la carte du portail avait déjà retiré la remise. Elle
 * reste affichée dans les deux cas — c'est la REMISE qui expire, jamais
 * l'éligibilité.
 *
 * Attention, la parité avec la carte n'est que PARTIELLE et l'était déjà : la
 * bannière annonce l'avantage sur la seule présence d'une date limite, quand la
 * carte exige en plus `recommendedOffer.loyalty_eligible`. Une famille non
 * éligible à la remise mais dans la fenêtre lit donc un avantage sur la home
 * qu'elle ne retrouve pas sur la carte. Non traité ici (l'ancienne formulation
 * avait exactement le même écart), mais à ne pas prendre pour de la parité.
 *
 * @param type          `upcoming_activation.type`.
 * @param firstName     Prénom de l'élève.
 * @param deadlineLabel Date limite de la remise, déjà formatée, ou null.
 */
export const getActivationHero = (type, firstName, deadlineLabel) => ({
    eyebrow: "2 minutes suffisent",
    headline: getActivationHeadline(type, firstName),
    subline: deadlineLabel
        ? `Confirmez avant le ${deadlineLabel} pour profiter de votre avantage ${getActivationSeasonWord(type)}`
        : "Sa place et ses horaires sont prêts à être confirmés.",
})

// Replis des SnackBars d'échec des modales d'activation. Le message du back
// prime quand il existe — c'est lui qui porte la raison actionnable (ex. une
// reprise pilotée par le choix d'été renvoie « contactez-nous ») ; ces libellés
// ne couvrent que la panne muette. En SnackBar et jamais en DialogM : un dialog
// ouvert depuis une modale rend DERRIÈRE elle (empilement de z-index), le
// parent ne verrait rien.
export const ACTIVATION_SAVE_FAILED_SNACKBAR =
    "Nous n'avons pas pu enregistrer votre choix. Réessayez dans un instant."

/**
 * « CE QUE VOUS GARDEZ » — toutes les promesses de non-enfermement de la carte
 * d'activation, en une seule liste.
 *
 * Contenu statique, arrêté avec les ops : ce sont les marges de manœuvre dont
 * ils voient qu'elles décident de l'activation au téléphone, et que la page
 * taisait. Le parent lisait un engagement fermé là où il n'y en a pas.
 *
 * Elle a absorbé les promesses du même registre qui vivaient ailleurs sur la
 * carte — la réassurance créneaux, en ligne isolée en tête de corps, et
 * l'annulabilité, qui traînait en pied. Un seul message, « vous n'êtes pas
 * enfermé », se trouvait éclaté en trois endroits et trois traitements visuels
 * que le parent devait recoller lui-même. L'ancienne réassurance créneaux vivait aussi
 * dans la modale de créneaux, retirée avec elle — le composeur porte tout.
 *
 * TROIS lignes et non cinq : les quatre droits de CHANGEMENT (créneau, formule,
 * matière, tuteur) tiennent en une, le deux-points faisant le travail des
 * virgules. Énumérés un par un, ils débordaient en 390 px et donnaient une liste
 * au peigne irrégulier ; regroupés, ils se lisent comme ce qu'ils sont — une
 * seule promesse, déclinée.
 *
 * ⚠️ « Annulable JUSQU'À LA REPRISE », et surtout pas « à tout moment ».
 * L'offre recommandée est posée AVEC engagement (`recommendedOffer()` fixe
 * `$withCommitment = true` en dur) et la remise de 50 € n'est accordée que sous
 * cet engagement (`$loyaltyEligible = $withCommitment && …`) : le parent qui
 * suit le chemin par défaut — celui que cette carte vend — s'engage douze mois.
 * Lui promettre ici une annulation sans condition serait faux. Ce qui est vrai à
 * cet instant, et le reste dans tous les cas, c'est qu'il peut revenir sur la
 * reprise qu'il planifie tant qu'elle n'a pas démarré : c'est déjà la formule du
 * mail de confirmation. L'engagement, lui, se divulgue là où il se contracte —
 * la section « Durée d'engagement » de la modale d'activation, qui porte aussi
 * la sortie « Je préfère zéro engagement ».
 *
 * L'engagement verrouille la DURÉE, pas le CONTENU : les deux autres lignes
 * (changements, pause) restent vraies sous engagement.
 *
 * Les montants et les offres n'ont RIEN à faire ici : cette liste énonce des
 * droits, pas un tarif.
 */
export const ACTIVATION_FLEXIBILITY_POINTS = [
    {
        // Même icône que la garantie « Changer de formule » du checkout
        // (`NewSubscriptionGuaranteesCard`) : une promesse identique ne se
        // signale pas par deux pictogrammes différents selon l'écran.
        icon: "sliders-h",
        label: "Créneau, formule, matière, tuteur : modifiables à tout moment",
    },
    {
        icon: "pause-circle",
        label: "Jusqu'à 3 mois de pause",
    },
    {
        icon: "undo-alt",
        label: "Annulable jusqu'à la reprise",
    },
]

/**
 * Ligne de prix UNIQUE de l'activation — « 1ᵉʳ mois 37 € puis 87 €/mois », ou
 * « 87 €/mois » sans remise.
 *
 * Point unique de formulation du prix : la carte « Et ensuite ? » (suite à
 * activer) et la carte « Reprise planifiée » doivent annoncer le prix avec les
 * mêmes mots, et une seule fois chacune. Le prix apparaissait auparavant en
 * deux lignes détaillées d'un côté (« Prix mensuel » + « 1ᵉʳ mois (remise
 * fidélité) ») et en une phrase de l'autre : le parent relisait trois montants
 * pour une seule offre.
 *
 * La remise n'est annoncée que si elle en est une (1ᵉʳ mois strictement
 * inférieur au mensuel) : sans ça, une égalité des deux montants ferait lire
 * « 1ᵉʳ mois 87 € puis 87 €/mois ».
 */
export const getActivationPriceLine = (monthlyPrice, firstMonthPrice) => {
    if (monthlyPrice == null) return null

    const monthly = `${formatActivationPrice(monthlyPrice)}/mois`
    const hasDiscount =
        firstMonthPrice != null && firstMonthPrice < monthlyPrice

    return hasDiscount
        ? `1ᵉʳ mois ${formatActivationPrice(firstMonthPrice)} puis ${monthly}`
        : monthly
}

/** Volume hebdomadaire plancher de la remise fidélité (miroir du back). */
export const LOYALTY_MIN_HOURS_PER_WEEK = 2

/**
 * Remise fidélité applicable à l'offre EN COURS D'ÉDITION dans la modale —
 * miroir exact de la règle du back (`SubscriptionActivationController::store`),
 * pour que le montant annoncé soit celui qui sera facturé.
 *
 * La modale affichait la remise dès que le champ `loyalty_eligible` était
 * absent du descripteur qu'on lui passait — ce qui est précisément le cas en
 * MODIFICATION, où l'offre vient du jalon planifié. Elle promettait donc
 * « 1ᵉʳ mois 69 € » sur des modifications que le back facturait plein tarif :
 * sur un jalon posé par l'équipe (qui n'accorde jamais de remise nouvelle), et
 * sur un re-plan passé la date limite. Un parent pouvait ainsi perdre 50 € en
 * ouvrant « Modifier » pour ajouter une matière.
 *
 * Trois entrées, trois règles :
 *  - `grantedAmount` (`planned.loyalty_discount_total`) : le montant RÉELLEMENT
 *    consenti sur la reprise déjà posée. Il prime sur le barème et survit à la
 *    fermeture de la fenêtre — ce qui est promis reste dû.
 *  - `catalogAmount` (`upcoming_activation.loyalty_discount_amount`) : le barème
 *    courant, pour une remise à accorder.
 *  - `windowOpen` (`upcoming_activation.discount_window_open`) : l'urgence
 *    court-elle encore ? Autorité du back, jamais recalculée ici.
 *
 * La modification IN-PLACE (jalon d'un autre canal) n'accorde jamais de remise
 * nouvelle côté back : seule celle déjà portée par le jalon compte.
 *
 * Un back non déployé (champs absents) rend `catalogAmount` nul, donc AUCUNE
 * remise affichée — on préfère taire une remise réelle que d'en promettre une
 * qui ne sera pas appliquée.
 */
export const getLoyaltyDiscount = ({
    grantedAmount = 0,
    catalogAmount = 0,
    windowOpen = false,
    inPlaceModification = false,
    withCommitment = false,
    hoursPerWeek = 0,
} = {}) => {
    const granted = Number(grantedAmount) || 0
    const amount = granted > 0 ? granted : Number(catalogAmount) || 0
    const grantable = inPlaceModification ? granted > 0 : granted > 0 || !!windowOpen

    const eligible =
        grantable &&
        amount > 0 &&
        !!withCommitment &&
        Number(hoursPerWeek) >= LOYALTY_MIN_HOURS_PER_WEEK

    return { eligible, amount: eligible ? amount : 0 }
}

/**
 * La couverture annoncée est-elle DERRIÈRE nous ?
 *
 * Le portail reste consultable après la fin du produit d'appel (la suite est
 * justement encore activable), donc la carte état doit savoir parler au passé.
 * Comparaison au JOUR : le dernier jour de couverture compte en entier.
 */
export const isProductCoverageOver = (endDate, now = undefined) =>
    !!(endDate && moment(endDate).isBefore(now ? moment(now) : moment(), "day"))

/**
 * Ligne de fin de couverture de la carte état — « se termine le 31 août 2026 »,
 * ou « s'est terminé le 31 juillet 2026 » quand la date est déjà passée.
 *
 * Cette phrase annonce une COUVERTURE QUI S'ARRÊTE. Elle n'est donc juste que
 * pour un produit BORNÉ — cours d'été, stage : la couverture s'arrête bel et
 * bien à la date annoncée, et la reprise est une décision à prendre.
 *
 * Une fin d'engagement ne borne RIEN : l'abonnement se poursuit au-delà, au même
 * prix, sans engagement. « se termine le 14 octobre » s'y lisait « mon
 * abonnement s'arrête » sur un abonnement qui se renouvelle. Ce type ne rend donc
 * jamais rien ici, et la carte garde sa ligne « Fin d'engagement : le … », qui
 * date une borne contractuelle sans annoncer d'arrêt.
 *
 * Le TEMPS de la phrase suit la date : lue un 5 août, « se termine le
 * 31 juillet » annonçait une échéance à venir alors qu'elle était derrière, et
 * la carte affichait dans la même colonne « Abonnement actif ».
 *
 * @param type      `upcoming_activation.type`.
 * @param endDate   `upcoming_activation.due_date` (date ISO ou null).
 * @param now       Instant de référence (injectable pour les tests).
 */
export const getProductEndLine = (type, endDate, now = undefined) => {
    const endLabel = formatActivationDate(endDate)
    if (!endLabel) return null
    if (type === "fin_engagement") return null

    return isProductCoverageOver(endDate, now)
        ? `s'est terminé le ${endLabel}`
        : `se termine le ${endLabel}`
}

/**
 * Trou de couverture entre la fin du produit d'appel et la reprise.
 *
 * La borne haute est le DERNIER JOUR NON COUVERT, soit la veille de la reprise —
 * le jour de reprise est couvert (les lives reprennent, le prélèvement repart),
 * donc l'inclure ferait dire « aucun live du 1ᵉʳ août au 1ᵉʳ septembre » un jour
 * où il y a justement des lives, en contradiction avec le CTA « jusqu'au
 * 31 août » juste en dessous.
 *
 * @param productEndDate Dernier jour couvert par le produit d'appel.
 * @param resumeDate     Jour de reprise (premier jour re-couvert).
 * @returns {{hasGap: boolean, startLabel: ?string, endLabel: ?string}}
 */
export const getCoverageGap = (productEndDate, resumeDate) => {
    const start = productEndDate ? moment(productEndDate).add(1, "day") : null
    const end = resumeDate ? moment(resumeDate).subtract(1, "day") : null
    // `isSameOrAfter` et non `isAfter` : un trou d'un seul jour est un vrai trou
    // (« aucun live le 31 août »), et il doit rester annonçable.
    const hasGap = !!(start && end && end.isSameOrAfter(start, "day"))

    if (!hasGap) {
        return { hasGap: false, startLabel: null, endLabel: null }
    }

    return {
        hasGap: true,
        startLabel: formatActivationDate(start),
        endLabel: formatActivationDate(end),
    }
}

/**
 * Le trou de couverture doit-il être ANNONCÉ ?
 *
 * Le bloc « Aucun live ni prélèvement du 1ᵉʳ août au 31 août » est une
 * information de DÉCISION : il prévient le parent d'une interruption à venir
 * pour qu'il puisse la combler (CTA « Prolonger ») ou la choisir en
 * connaissance de cause. Il perd tout sens une fois la couverture derrière
 * nous : la période est déjà entamée, et le back a retiré `extendable_to` (une
 * borne de facturation passée ne se déplace plus — cf. `resolveExtendableTo`),
 * donc plus aucun bouton ne l'accompagne. Le parent lisait alors un constat
 * orphelin sur un trou qu'il ne pouvait plus éviter.
 *
 * @param hasGap         `getCoverageGap().hasGap`.
 * @param productEndDate Dernier jour couvert par le produit d'appel.
 * @param now            Instant de référence (injectable pour les tests).
 */
export const shouldAnnounceCoverageGap = (
    hasGap,
    productEndDate,
    now = undefined,
) => !!hasGap && !isProductCoverageOver(productEndDate, now)

/**
 * Compte à rebours de la remise fidélité.
 *
 * @param discountExpiresAt Date ISO posée par l'évaluateur (1ᵉʳ live du produit
 *                          + fenêtre, planchée sur la date de lancement), ou
 *                          null quand il n'y a pas encore de live tenu.
 * @param now               Instant de référence (injectable pour les tests).
 * @returns {{stillOpen: boolean, deadlineLabel: ?string, daysLeft: ?number, urgencyLabel: ?string}}
 */
export const getDiscountUrgency = (discountExpiresAt, now = undefined) => {
    const reference = now ? moment(now) : moment()
    const deadline = discountExpiresAt ? moment(discountExpiresAt) : null
    // Comparaison au JOUR : le dernier jour de l'offre compte en entier, sinon
    // une remise expirant « aujourd'hui » disparaîtrait dès 00:01.
    const stillOpen = !!(deadline && deadline.isSameOrAfter(reference, "day"))

    if (!stillOpen) {
        return {
            stillOpen: false,
            deadlineLabel: null,
            daysLeft: null,
            urgencyLabel: null,
        }
    }

    const daysLeft = deadline
        .clone()
        .startOf("day")
        .diff(reference.clone().startOf("day"), "days")

    return {
        stillOpen: true,
        deadlineLabel: formatActivationDate(deadline),
        daysLeft,
        urgencyLabel:
            daysLeft === 0
                ? "Dernier jour"
                : daysLeft === 1
                  ? "Plus qu'un jour"
                  : `Plus que ${daysLeft} jours`,
    }
}


/**
 * Le bloc `upcoming_activation` du portail Abonnement, fusionné entre les DEUX
 * charges que l'écran mélange dans son `user` :
 *
 *  - `currentUser.object` — GET `/api/user`, route `auth:sanctum` ;
 *  - `formData.user` — GET `/api/v2/subscribe/pricing`, route `auth_or_email`.
 *
 * Les deux Resources appellent pourtant le même `evaluateWithAdminContext()`.
 * Ce qui les sépare est l'ORDRE DES MIDDLEWARES : `ResolveImpersonationContext`
 * vit dans le groupe `api`, donc avant les middlewares de route — sauf que
 * `Authenticate` (ce que résout `auth:sanctum`) est listé dans
 * `$middlewarePriority` et se fait remonter AVANT le groupe. Sur `/api/user`,
 * l'utilisateur est donc résolu quand le contexte d'impersonation se pose ; sur
 * `/api/v2/subscribe/pricing`, `auth_or_email` n'est pas prioritaire, tourne
 * après, et le contexte se pose sur une requête sans utilisateur — attribut
 * absent, Resource sans clés admin. Vérifié en sandbox : sous un vrai jeton
 * d'impersonation, `/api/user` sert `admin_resume_dates`, pas la charge pricing.
 *
 * Or `mapStateToProps` étale les deux objets utilisateur l'un sur l'autre, et un
 * spread REMPLACE une valeur imbriquée au lieu de la fusionner : l'objet
 * `upcoming_activation` de la charge sans contexte écrasait entièrement celui de
 * la charge impersonation-aware, emportant les clés admin avec lui. Même piège
 * que celui qui avait forcé le re-lift explicite des champs `summer_*` dans ce
 * même sélecteur.
 *
 * On fusionne donc clé à clé, sur deux niveaux (`upcoming_activation` et son
 * `planned`, qui porte lui aussi une clé admin — `cancellable_by_admin`). La
 * charge la plus FRAÎCHE reste prioritaire sur les clés qu'elle possède : elle
 * est rafraîchie au montage de l'écran, et c'est le comportement d'aujourd'hui.
 * Seules survivent en plus les clés qu'elle ne porte pas du tout.
 *
 * Aucun objet n'est FABRIQUÉ : deux absences rendent l'absence, sinon le portail
 * lirait un bloc vide là où il attend `null`.
 */
export const mergeUpcomingActivation = (withAdminKeys, fresher) => {
    if (!withAdminKeys) return fresher ?? null
    if (!fresher) return withAdminKeys

    const merged = { ...withAdminKeys, ...fresher }

    // Fusion du second niveau UNIQUEMENT quand les deux charges portent un
    // jalon. Sinon on laisse gagner le spread de tête, qui donne déjà la bonne
    // réponse — et surtout : une reprise ANNULÉE arrive en `planned: null` sur
    // la charge fraîche, et la ressusciter à partir de la charge d'à côté
    // afficherait une reprise planifiée qui n'existe plus.
    if (withAdminKeys.planned && fresher.planned) {
        merged.planned = { ...withAdminKeys.planned, ...fresher.planned }
    }

    return merged
}

/**
 * Les options de date de reprise de la modale d'activation : les dates
 * PROPOSÉES par le back (`summer_config.activation_resume_dates` — 1ᵉʳ, 7,
 * 14 septembre par défaut), plus, en MODIFICATION, la date du jalon existant
 * quand elle ne correspond à aucune proposition (jalon ancien / posé par un
 * autre canal à une date hors liste) : la re-défaulter silencieusement au
 * 1ᵉʳ septembre ferait déplacer la reprise d'un parent qui ne voulait que
 * changer sa formule.
 *
 * Sous impersonation, le back sert une liste PAR UTILISATEUR
 * (`upcoming_activation.admin_resume_dates`) qui ajoute le 1ᵉʳ octobre aux
 * trois dates parent : elle REMPLACE la liste globale quand elle est là, parce
 * qu'elle en est déjà un sur-ensemble. Un parent seul ne la reçoit pas, sauf
 * après un stage tenu pendant l'année : elle porte alors sa date par défaut
 * (le lundi qui suit le stage), celle de l'été ne le concernant pas. Elle
 * n'est retenue que non vide — un back pas encore déployé ne doit pas vider le
 * picker. Ce n'est qu'un AFFICHAGE : la garde est le refus du POST hors
 * impersonation, côté serveur.
 *
 * Les dates proposées PASSÉES ne s'affichent plus : le back les refuse de
 * toute façon au POST (`resume_date_past`) — le picker offrait donc un choix
 * mort (le 1ᵉʳ septembre encore affiché le 2). La date du JALON
 * (`currentDate`) n'est volontairement PAS filtrée, même passée : c'est la
 * vérité de la reprise en cours, on n'invente pas une date à sa place.
 *
 * @param proposedDates  `summer_config.activation_resume_dates` (ISO strings).
 * @param adminDates     `upcoming_activation.admin_resume_dates`, ou null.
 * @param currentDate    La date du jalon existant (modification), ou null.
 * @param now            Référence « aujourd'hui » (tests) ; défaut : maintenant.
 * @returns {{ options: Array<{value: string, adHoc: boolean}>, selected: string|null }}
 *          options triées ; `selected` = la date à présélectionner (la date
 *          courante en modification, sinon la première proposée à venir).
 */
export const getResumeDateOptions = ({
    proposedDates = [],
    adminDates = null,
    currentDate = null,
    now = null,
} = {}) => {
    // Comparaison de chaînes ISO `YYYY-MM-DD`, comme le tri plus bas — même
    // convention. `moment()` sans timezone explicite = heure locale de
    // l'appareil, la convention de tout ce fichier (cf. isProductCoverageOver).
    const todayIso = (now ? moment(now) : moment()).format("YYYY-MM-DD")
    const granted = (Array.isArray(adminDates) ? adminDates : []).filter(Boolean)
    const proposed = (granted.length
        ? granted
        : (proposedDates ?? []).filter(Boolean)
    ).filter((value) => value >= todayIso)
    const options = proposed.map((value) => ({ value, adHoc: false }))

    if (currentDate && !proposed.includes(currentDate)) {
        options.push({ value: currentDate, adHoc: true })
    }
    options.sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0))

    return {
        options,
        selected: currentDate ?? proposed[0] ?? null,
    }
}

/**
 * Le CTA de prolongation du produit d'appel est-il ACTIONNABLE ?
 *
 * Miroir exact du garde-fou d'écriture : `trajectoryConflict()` refuse la
 * prolongation (409 `activation_planned`) dès qu'une reprise vivante existe,
 * parce que déplacer la fin de saison décalerait le raccord sous une suite déjà
 * vendue. Sans ce miroir, la page offrait un bouton qui ne pouvait qu'échouer.
 *
 * Ne dit rien du BLOC « trou de couverture », qui reste affiché même sans
 * bouton : c'est une information décisionnelle pour le parent.
 *
 * @param extendableTo       `upcoming_activation.extendable_to` (null = rien à
 *                           prolonger : pas de trou, ou couverture expirée).
 * @param hasPlannedActivation Une reprise est déjà posée.
 */
export const canExtendProduct = (extendableTo, hasPlannedActivation) =>
    !!extendableTo && !hasPlannedActivation

/**
 * La répartition est-elle indexée par POSITION au lieu de l'être par matière ?
 *
 * Garde-fou de DÉFENSE EN PROFONDEUR, conservé même après le passage au contrat
 * en liste d'objets : un jalon écrit avant ce contrat peut encore porter une map
 * réindexée. Une lecture par identifiant sur une telle liste touche l'INDICE —
 * matières `[1, 4, 2]` × `[1, 0.5, 0.5]` affichait « Maths · 0.5h, Français
 * (sans volume), Physique-Chimie · 0.5h » : chaque matière portait le volume de
 * sa voisine, et la dernière n'en avait plus.
 *
 * Une clé `0` suffit à trancher : aucun identifiant de matière ne vaut 0. Mieux
 * vaut n'afficher AUCUN volume que d'en afficher de faux — le nom des matières,
 * lui, reste juste.
 */
const isPositionalAllocation = (allocation) =>
    Array.isArray(allocation) ||
    Object.prototype.hasOwnProperty.call(allocation, "0")

/**
 * La répartition est-elle la LISTE d'objets `[{subject_id, hours}]` que le back
 * projette ?
 *
 * C'est la forme CANONIQUE en transport, et elle existe pour une raison précise :
 * une Resource Laravel réindexe en liste tout sous-tableau dont les clés sont
 * toutes numériques — ce que sont les identifiants de matière. La map
 * `{"1":1,"2":0.5}` arrivait donc ici en `[1, 0.5]`, décalée d'un cran, et le
 * garde-fou positionnel taisait alors TOUS les volumes : aucun n'a jamais
 * pu s'afficher. La liste d'objets nomme sa matière, donc rien ne peut plus
 * glisser en route.
 */
const isAllocationList = (allocation) =>
    Array.isArray(allocation) &&
    allocation.length > 0 &&
    allocation.every(
        (entry) => entry && typeof entry === "object" && "subject_id" in entry,
    )

/**
 * Répartition ramenée à la map `{subject_id: heures}` que la lecture attend,
 * quelle que soit la forme reçue (liste d'objets du back, ou map saisie dans la
 * modale avant l'aller-retour serveur). Une forme positionnelle non nommée est
 * vidée : mieux vaut aucun volume que des volumes faux.
 */
export const allocationBySubject = (raw) => {
    if (isAllocationList(raw)) {
        return raw.reduce((acc, { subject_id: id, hours }) => {
            acc[id] = hours
            return acc
        }, {})
    }

    return isPositionalAllocation(raw) ? {} : raw
}

/**
 * Matières choisies pour la suite, décrites comme des CHIPS — une par matière,
 * portant son volume quand la répartition a été renseignée (« Maths · 2h »).
 *
 * Le descripteur produit est exactement celui que rend la carte de l'abonnement
 * en cours (`{subjects, label, hoursSuffix}`) : la même matière ne doit pas
 * s'annoncer en chip d'un côté et en ligne de texte de l'autre — le parent lisait
 * alors deux produits différents là où il n'y en a qu'un. La carte de la suite
 * n'a en revanche pas d'objectif pédagogique à grouper (la suite n'a pas
 * commencé), d'où UNE chip par matière et non par objectif.
 *
 * Accepte la liste d'objets projetée par le back comme la map saisie côté
 * modale. Les clés d'une map reviennent du JSON du jalon, donc parfois en
 * string : on tolère les deux plutôt que de perdre silencieusement le volume.
 * Une matière que le référentiel ne résout pas est omise, jamais affichée sous
 * forme d'identifiant.
 *
 * @param resolveSubject (id) => ?{id, name, icon, color} — référentiel matières.
 * @param keyPrefix Préfixe de clé de rendu (une carte peut en afficher deux jeux).
 */
export const getSubjectHoursChips = (
    subjectIds,
    hoursAllocation,
    resolveSubject,
    keyPrefix = "suite-subject",
) => {
    if (!Array.isArray(subjectIds) || subjectIds.length === 0) return []
    const allocation = allocationBySubject(hoursAllocation ?? {})

    return subjectIds
        .map((id) => {
            const subject = resolveSubject?.(id)
            if (!subject?.name) return null
            const hours = allocation[id] ?? allocation[String(id)]

            return {
                key: `${keyPrefix}-${id}`,
                subjects: [subject],
                label: subject.name,
                // Même suffixe que les chips de l'abo en cours : l'unité est
                // portée par la ligne d'offre au-dessus (« · 3h/sem »).
                hoursSuffix: hours > 0 ? ` · ${hours}h` : "",
            }
        })
        .filter(Boolean)
}

// Les conversions de créneaux vivent avec la grille, sous `src/data`, pour que
// les ports de l'espace parent les lisent aussi : ré-exportées ici pour les
// lecteurs legacy.
export {
    canonicalSlotsToGridSlots,
    canonicalSlotsToSelectableGridSlots,
    keepSelectableGridSlots,
} from "@data/utility/constraintsGrid"
